import { apiErrorField } from '@agos/api-client'
import {
  getGetSettingsPermissionsQueryKey,
  useGetSettingsPermissions,
  useGetSettingsPermissionsAudit,
  useGetSettingsPermissionsEmployees,
  usePutSettingsPermissionsEmployeeId,
  usePutSettingsPermissionsRole,
} from '@agos/api-client/auth'
import { T, formatCount, tt, useAuth } from '@agos/shared'
import {
  ConfirmDialog,
  FilterBar,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ChevronRight, History, Search, Shield, UserCog, Users } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  permissionLabel,
  actionText,
  auditChange,
  byMostRecent,
  byOverridesThenName,
  matchingEmployees,
  childrenOf,
  cycleOverride,
  defeatedDenials,
  displayChildrenOf,
  effectiveCount,
  grantedCount,
  grantedViaParent,
  groupCatalogue,
  heldBecause,
  isDirty,
  matchesSearch,
  overrideCount,
  overrideDiff,
  overridePayload,
  overridesAreDirty,
  overrideState,
  parentOf,
  parseStamp,
  permissionDescription,
  roleDiff,
  roleLabel,
  rolePayload,
  switchState,
  toggled,
  withAll,
  withOverride,
  displayParentOf,
  type AuditEntry,
  type EmployeePermissions,
  type Override,
  type OverrideState,
  type PermissionsData,
  type SwitchState,
} from './permission-matrix'

/**
 * `/permissions` — what each role may do, what one employee may do differently, and who changed it.
 *
 * **FB-103.** A guard names a family and accepts either member —
 * `@RequirePermission({"fiskal", "fiskal_storno"})` — so holding the parent grants the child whatever
 * the child's own switch says. Legacy declares the relationship in `SUB_OF`, `void`s it, and renders
 * every child as an independent toggle whose only hint of the hierarchy is two spaces and an arrow in
 * the label. **In this data 16 switches read *off* while the access is granted**, and among them are
 * `fiskal_storno` and `fiskal_closure` for `waiter` - voiding a fiscal receipt and running a closure.
 * `online_payments_refund` was a third until `V51` (BACKEND.md §10A.134).
 *
 * The state is now named: on, off, or *held through the parent*. Saving previews what a parent change
 * does to its children. And the family is derived from the guards rather than the key shape, because
 * `fiskal_print` and `attendance_view` look like children and no guard pairs them.
 *
 * Ported from legacy `pages/permissions.tsx` (1 285 lines).
 */

type Tab = 'roles' | 'employees' | 'audit'

/** §9.152 — how much of the catalogue the exceptions tab is showing. */
type OverrideScope = 'exceptions' | 'granted' | 'all'

export function PermissionsPage() {
  const [tab, setTab] = useState<Tab>('roles')

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Práva')}
        description={tt('Čo smie každá rola, čo smie jeden človek inak, a kto to zmenil.')}
        icon={<Shield className="w-5 h-5" />}
      />

      <TabGroup
        variant="bar"
        tabs={[
          { id: 'roles', label: tt('Role'), icon: Users },
          { id: 'employees', label: tt('Výnimky'), icon: UserCog },
          { id: 'audit', label: tt('História'), icon: History },
        ]}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      {tab === 'roles' && <RolesTab />}
      {tab === 'employees' && <OverridesTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  )
}

// ─── roles ───────────────────────────────────────────────────────────────────

function RolesTab() {
  const { refreshPermissions, user } = useAuth()
  const ownRole = user?.systemRole ?? ''
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data, isLoading } = useGetSettingsPermissions()
  const payload = (data ?? {}) as PermissionsData
  const catalogue = useMemo(() => payload.allPermissions ?? [], [payload.allPermissions])
  const known = useMemo(() => new Set(catalogue), [catalogue])
  const roles = payload.editableRoles ?? []

  const [role, setRole] = useState('admin')
  const [search, setSearch] = useState('')
  const [local, setLocal] = useState<Record<string, Set<string>> | null>(null)
  const [confirming, setConfirming] = useState(false)

  const stored = useMemo(() => new Set(payload.permissions?.[role] ?? []), [payload.permissions, role])
  const granted = local?.[role] ?? stored
  const diff = roleDiff(stored, granted, catalogue)
  const viaParent = grantedViaParent(granted, catalogue)
  const groups = useMemo(() => groupCatalogue(catalogue), [catalogue])
  // §9.176 — which groups the operator has opened. Collapsed is the default, so the set holds the
  // exceptions rather than the rule: an empty set is the screen as it first draws.
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set())

  const setGranted = (next: Set<string>) => setLocal({ ...(local ?? {}), [role]: next })

  const save = usePutSettingsPermissionsRole({
    mutation: {
      onSuccess: (result: unknown) => {
        const rejected = (result as { rejected?: string[] }).rejected ?? []
        setConfirming(false)
        setLocal((current) => {
          if (current === null) return null
          const next = { ...current }
          delete next[role]
          return next
        })
        void queryClient.invalidateQueries({ queryKey: getGetSettingsPermissionsQueryKey() })
        // The editor's own session holds the old set until the context polls again — three minutes,
        // or the next focus. Editing your own role and then finding the sidebar unchanged reads as a
        // failed save, so the session is refreshed here rather than waited for.
        void refreshPermissions()
        toast({
          title: `${tt('Uložené pre rolu')} ${roleLabel(role)}`,
          description:
            rejected.length > 0
              ? `${tt('Server odmietol neznáme kľúče:')} ${rejected.join(', ')}`
              : ownRole === role
                ? tt('Vaše vlastné oprávnenia sa práve obnovili. Ostatní prihlásení to uvidia do troch minút.')
                : tt('Prihlásení zamestnanci to uvidia do troch minút alebo po prepnutí na okno.'),
          variant: rejected.length > 0 ? 'destructive' : undefined,
        })
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Práva sa neuložili.'),
          variant: 'destructive',
        }),
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <TabGroup
        tabs={roles.map((candidate) => ({
          id: candidate,
          label: `${roleLabel(candidate)} (${grantedCount(local?.[candidate] ?? new Set(payload.permissions?.[candidate] ?? []), catalogue)})`,
        }))}
        active={role}
        onChange={setRole}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-role-totals>
        <StatCard label={tt('Zapnuté')} value={formatCount(grantedCount(granted, catalogue))} icon={Shield} color="blue" />
        <StatCard
          label={tt('Skutočne platí')}
          value={formatCount(effectiveCount(granted, catalogue))}
          icon={Shield}
          color="violet"
        />
        <StatCard
          label={tt('Cez nadradené')}
          value={formatCount(viaParent.length)}
          icon={AlertCircle}
          color={viaParent.length > 0 ? 'amber' : 'emerald'}
        />
        <StatCard label={tt('V katalógu')} value={formatCount(catalogue.length)} icon={Users} color="emerald" />
      </div>

      {/* FB-103 - the switches that read off while the access is granted. */}
      {viaParent.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-via-parent>
          <p className="text-xs font-bold text-amber-500">
            <strong>{viaParent.length}</strong>{' '}
            <T>
              práv je vypnutých na prepínači a napriek tomu platia, pretože ich udeľuje nadradené
              právo.
            </T>
          </p>
          <p className="text-xs text-amber-500">
            {viaParent
              .map((permission) => `${permission} ← ${parentOf(permission, known) ?? ''}`)
              .join(' · ')}
          </p>
        </div>
      )}

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať právo...') }}
      />

      {isDirty(diff) && (
        <div className="sticky top-0 z-10 rounded-xl border-2 border-primary/40 bg-primary/5 p-3 space-y-1.5" data-role-diff>
          <p className="text-xs font-bold">
            {diff.added.length} <T>pridaných</T> · {diff.removed.length} <T>odobraných</T>
          </p>
          {diff.added.length > 0 && (
            <p className="text-xs text-emerald-500" data-diff-added>
              + {diff.added.join(', ')}
            </p>
          )}
          {diff.removed.length > 0 && (
            <p className="text-xs text-red-500" data-diff-removed>
              − {diff.removed.join(', ')}
            </p>
          )}
          {/* Legacy's preview stops at the ticked set; a parent change moves its children too. */}
          {diff.sideEffects.length > 0 && (
            <p className="text-xs text-amber-500" data-diff-side-effects>
              <T>Zároveň sa</T>{' '}
              {diff.sideEffects[0]?.nowHeld ? <T>udelí</T> : <T>odoberie</T>}{' '}
              <strong>{diff.sideEffects.length}</strong>{' '}
              <T>podradených práv, bez toho aby sa ich prepínač pohol:</T>{' '}
              {diff.sideEffects.map((effect) => effect.permission).join(', ')}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() =>
                setLocal((current) => {
                  if (current === null) return null
                  const next = { ...current }
                  delete next[role]
                  return next
                })
              }
              className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
            >
              <T>Zahodiť</T>
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={save.isPending}
              className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
            >
              <T>Uložiť</T>
            </button>
          </div>
        </div>
      )}

      {payload.defaults?.[role] !== undefined && (
        <button
          type="button"
          onClick={() => setGranted(new Set(payload.defaults?.[role] ?? []))}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
        >
          <T>Načítať odporúčané pre túto rolu</T>
        </button>
      )}

      <div className="space-y-3" data-permission-groups>
        {groups.map((group) => {
          const visible = group.entries.filter((entry) =>
            // §9.145 — searching for *Vydané faktúry* must find `outgoing_invoices`.
            matchesSearch(entry.permission, permissionLabel(entry.permission, entry.parent), search),
          )
          if (visible.length === 0) return null
          const allOn = visible.every((entry) => granted.has(entry.permission))
          const onCount = visible.filter((entry) => granted.has(entry.permission)).length
          // §9.176 — a search overrides the collapse. §9.145 made the search match a permission's
          // label as well as its slug, and a collapsed group would hide the hit while the screen
          // said nothing was found. So a typed search opens every group that still has a row.
          const isOpen = search.trim() !== '' || opened.has(group.key)

          return (
            <div key={group.key} className="rounded-2xl border border-border bg-card p-4 space-y-2" data-permission-group={group.key}>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  data-group-toggle={group.key}
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpened((current) => {
                      const next = new Set(current)
                      if (next.has(group.key)) next.delete(group.key)
                      else next.add(group.key)
                      return next
                    })
                  }
                  className="flex items-center gap-2 min-w-0 flex-1 text-left"
                >
                  <ChevronRight
                    className={cn(
                      'w-4 h-4 shrink-0 text-muted-foreground transition-transform',
                      isOpen && 'rotate-90',
                    )}
                  />
                  <h3 className="text-sm font-bold truncate">{group.heading}</h3>
                  {/* A collapsed group that says nothing is a worse wall than an open one. */}
                  <span
                    className="text-[11px] text-muted-foreground tabular-nums shrink-0"
                    data-group-count={group.key}
                  >
                    {formatCount(onCount)} / {formatCount(visible.length)}
                  </span>
                </button>
                {/* Left on the header, where it works on a collapsed group too - it acts on the
                    group's visible rows exactly as before, and making somebody expand a group to
                    grant it would be a step for nothing. */}
                <button
                  type="button"
                  onClick={() =>
                    setGranted(withAll(granted, visible.map((entry) => entry.permission), !allOn))
                  }
                  aria-label={`${allOn ? tt('Vypnúť všetko') : tt('Zapnúť všetko')} - ${group.heading}`}
                  className="shrink-0 px-2.5 py-1 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted/50"
                >
                  {allOn ? <T>Vypnúť všetko</T> : <T>Zapnúť všetko</T>}
                </button>
              </div>
              {isOpen &&
                visible.map((entry) => (
                  <PermissionRow
                    key={entry.permission}
                    permission={entry.permission}
                    parent={entry.parent}
                    state={switchState(entry.permission, granted, known)}
                    covers={childrenOf(entry.permission, catalogue)}
                    shows={displayChildrenOf(entry.permission, catalogue)}
                    onToggle={() => setGranted(toggled(granted, entry.permission))}
                  />
                ))}
            </div>
          )
        })}
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => save.mutate({ role, data: rolePayload(granted, catalogue) })}
        title={`${tt('Uložiť práva pre')} ${roleLabel(role)}?`}
        description={
          diff.sideEffects.length > 0
            ? `${tt('Zmení sa aj')} ${diff.sideEffects.length} ${tt('podradených práv, ktoré udeľuje nadradené právo.')}`
            : tt('Prihlásení zamestnanci to uvidia po ďalšom načítaní.')
        }
        confirmLabel={tt('Uložiť')}
        loading={save.isPending}
      />
    </div>
  )
}

function PermissionRow({
  permission,
  parent,
  state,
  covers,
  shows,
  onToggle,
}: {
  permission: string
  parent: string | null
  state: SwitchState
  covers: string[]
  shows: string[]
  onToggle: () => void
}) {
  // §9.145 — a name for the row, not the slug it is stored under.
  const label = permissionLabel(permission, parent)
  const description = permissionDescription(permission)

  return (
    <div
      className={cn('flex items-center gap-3', parent !== null && 'pl-6')}
      data-permission-row={permission}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={permission}
        aria-pressed={state === 'on'}
        data-switch-state={state}
        className={cn(
          'w-10 h-5 rounded-full shrink-0 relative transition-colors',
          state === 'on' && 'bg-primary',
          state === 'via-parent' && 'bg-amber-500/50',
          state === 'off' && 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
            state === 'on' ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold truncate">
          {label}
          {parent === null && covers.length > 0 && (
            <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
              <T>udeľuje aj</T> {covers.length}
            </span>
          )}
          {parent === null && shows.length > covers.length && (
            <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
              ({shows.length - covers.length} <T>samostatne</T>)
            </span>
          )}
        </p>
        {/* §9.107 - legacy's own sentence, so granting a right is not done off a slug. A permission
          * the catalogue has and legacy did not simply shows nothing here. */}
        {description !== null && (
          <p className="text-[11px] text-muted-foreground" data-permission-description>
            {description}
          </p>
        )}
        {parent !== null && (
          <p className="text-[10px] text-muted-foreground font-mono truncate">{permission}</p>
        )}
      </div>
      {/* FB-103 - said on the row, not only in the summary. */}
      {state === 'via-parent' && (
        <span className="text-[10px] text-amber-500 shrink-0" data-via-parent-row>
          <T>platí cez</T> {parent}
        </span>
      )}
    </div>
  )
}

// ─── one employee's exceptions ───────────────────────────────────────────────

function OverridesTab() {
  const { refreshPermissions } = useAuth()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data, isLoading } = useGetSettingsPermissionsEmployees()
  const payload = (data ?? {}) as { allPermissions?: string[]; employees?: EmployeePermissions[] }
  const catalogue = useMemo(() => payload.allPermissions ?? [], [payload.allPermissions])
  const known = useMemo(() => new Set(catalogue), [catalogue])
  /**
   * §9.238 — legacy's employee search on this tab, which AGOS dropped.
   *
   * Matches **name, e-mail or role**, exactly as `permissions.tsx:856` does, so *sofer* finds every
   * driver. Twenty-two active employees here and no way to reach one was the tab the owner called
   * *"much harder… when setting custom permissions for specific user"*.
   */
  const [employeeSearch, setEmployeeSearch] = useState('')

  const employees = useMemo(
    () => byOverridesThenName(matchingEmployees(payload.employees ?? [], employeeSearch)),
    [payload.employees, employeeSearch],
  )

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [local, setLocal] = useState<Override[] | null>(null)
  const [search, setSearch] = useState('')
  /**
   * §9.214 — which groups are open, exactly as the roles tab holds them.
   *
   * Collapsed is the default, so the set holds the exceptions rather than the rule: an empty set is
   * the screen as it first draws.
   */
  const [openedGroups, setOpenedGroups] = useState<ReadonlySet<string>>(new Set())
  const groups = useMemo(() => groupCatalogue(catalogue), [catalogue])

  const selected = employees.find((employee) => employee.id === selectedId) ?? null
  /**
   * §9.152 — which rows are worth looking at, remembered **per employee** and derived rather than
   * reset in an effect.
   *
   * The scope has to go back to *Všetko* when a different person is opened, and doing that with a
   * `useEffect` calling `setScope` is a cascading render the lint rule rightly refuses. Pairing the
   * scope with the id it belongs to says the same thing during render: a scope chosen for somebody
   * else simply does not apply.
   *
   * **The default is *Všetko*, and the first version was wrong about that.** It narrowed to the
   * exceptions when the employee had any. Five tests failed and were right: narrowing hides every
   * inheriting row, so the one thing you cannot do from the default view is *add* an exception —
   * the flow the owner called hard.
   */
  const [scopeFor, setScopeFor] = useState<{ id: number | null; scope: OverrideScope }>({
    id: null,
    scope: 'all',
  })
  const scope: OverrideScope = scopeFor.id === (selected?.id ?? null) ? scopeFor.scope : 'all'
  const setScope = (next: OverrideScope) => setScopeFor({ id: selected?.id ?? null, scope: next })

  const overrides = local ?? selected?.overrides ?? []
  const exceptionCount = catalogue.filter(
    (permission) => overrideState(permission, overrides) !== 'inherit',
  ).length

  const denied = overrides.filter((override) => override.granted !== true).map((override) => override.permission ?? '')
  const roleGranted = new Set(selected?.rolePermissions ?? [])
  const defeated = defeatedDenials(denied, roleGranted, catalogue)
  const dirty = local !== null
  // The roles tab has had a preview since it was ported; this tab is where a mistake is quieter — an
  // exception applies to one person and nobody else can see it is there.
  const diff = overrideDiff(selected?.overrides ?? [], overrides, catalogue)

  const save = usePutSettingsPermissionsEmployeeId({
    mutation: {
      onSuccess: () => {
        setLocal(null)
        void queryClient.invalidateQueries()
        // An exception is per employee, so it only moves this session when it is this employee's.
        void refreshPermissions()
        toast({ title: tt('Výnimky uložené') })
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Výnimky sa neuložili.'),
          variant: 'destructive',
        }),
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="space-y-1.5" data-override-employees>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-employee-search
            value={employeeSearch}
            onChange={(event) => setEmployeeSearch(event.target.value)}
            placeholder={tt('Hľadať zamestnanca…')}
            className="w-full pl-9 pr-3 py-2 mb-1.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        {employees.length === 0 && (
          <p className="text-xs text-muted-foreground px-1 py-3" data-no-employees>
            <T>Nikto nezodpovedá hľadaniu.</T>
          </p>
        )}
        {employees.map((employee) => (
          <button
            key={employee.id}
            type="button"
            data-employee={employee.id}
            onClick={() => {
              setSelectedId(employee.id ?? null)
              setLocal(null)
            }}
            className={cn(
              'w-full flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors',
              employee.id === selectedId ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50',
            )}
            data-override-employee={employee.id}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold truncate">{employee.name}</span>
              <span className="block text-[11px] text-muted-foreground">
                {roleLabel(employee.systemRole ?? '')}
              </span>
            </span>
            {overrideCount(employee) > 0 && (
              <StatusBadge label={String(overrideCount(employee))} color="amber" />
            )}
          </button>
        ))}
      </div>

      <div className="lg:col-span-2 space-y-3">
        {selected === null ? (
          <p className="text-xs text-muted-foreground py-8 text-center">
            <T>Vyberte zamestnanca.</T>
          </p>
        ) : (
          <>
            {overridesAreDirty(diff) && (
              <div
                className="sticky top-0 z-10 rounded-xl border-2 border-primary/40 bg-primary/5 p-3 space-y-1.5"
                data-override-diff
              >
                {diff.granted.length > 0 && (
                  <p className="text-xs text-emerald-500" data-override-granted>
                    + {diff.granted.join(', ')} - <T>povolené len tomuto zamestnancovi</T>
                  </p>
                )}
                {diff.denied.length > 0 && (
                  <p className="text-xs text-red-500" data-override-denied>
                    − {diff.denied.join(', ')} - <T>zakázané len tomuto zamestnancovi</T>
                  </p>
                )}
                {/* Returning to the role is not the same as denying, and undoing one is not undoing
                  * the other — so it is counted apart rather than folded into the removals. */}
                {diff.returnedToRole.length > 0 && (
                  <p className="text-xs text-amber-500" data-override-returned>
                    ↺ {diff.returnedToRole.join(', ')} - <T>späť podľa roly</T>
                  </p>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-border bg-card p-4 space-y-1">
              <h3 className="text-sm font-bold">{selected.name}</h3>
              <p className="text-xs text-muted-foreground">
                <T>Rola</T> {roleLabel(selected.systemRole ?? '')} ·{' '}
                {formatCount((selected.effectivePermissions ?? []).length)} <T>práv v platnosti</T> ·{' '}
                {overrides.length} <T>výnimiek</T>
              </p>
              <p className="text-[11px] text-muted-foreground">
                <T>
                  Výnimka platí navyše k role: udelenie pridá právo, odobranie ho vezme. Bez výnimky
                  rozhoduje rola.
                </T>
              </p>
            </div>

            {/* FB-103's second direction, said where it could bite. */}
            {defeated.length > 0 && (
              <p className="flex items-start gap-1.5 text-xs text-red-500" data-defeated-denials>
                <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  <T>Toto odobranie nezaberie - právo je dostupné aj cez</T>{' '}
                  {defeated
                    .map((item) => `${item.permission} (${item.reachableVia.join(', ')})`)
                    .join(' · ')}
                  .
                </span>
              </p>
            )}

            {dirty && (
              <div className="flex items-center gap-2 rounded-xl border-2 border-primary/40 bg-primary/5 p-3" data-override-dirty>
                <p className="text-xs font-bold flex-1">
                  {overrides.length} <T>výnimiek pripravených</T>
                </p>
                <button
                  type="button"
                  onClick={() => setLocal(null)}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
                >
                  <T>Zahodiť</T>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    selected.id !== undefined &&
                    save.mutate({
                      id: selected.id,
                      data: overridePayload(overrides, catalogue),
                    })
                  }
                  disabled={save.isPending}
                  className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
                >
                  <T>Uložiť</T>
                </button>
              </div>
            )}

            <FilterBar
              search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať právo...') }}
              actions={
                /* §9.152 — the owner's *"much harder … for specific user"*. Four exceptions in 123
                 * rows cannot be found by reading, and the count is on the button so it is
                 * discoverable without being the default. */
                <div className="flex items-center gap-1" data-override-scope>
                  {(
                    [
                      ['exceptions', `${tt('Len výnimky')}${exceptionCount > 0 ? ` (${exceptionCount})` : ''}`],
                      ['granted', tt('Len udelené')],
                      ['all', tt('Všetko')],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setScope(id)}
                      data-scope={id}
                      data-scope-active={scope === id}
                      className={cn(
                        'px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors',
                        scope === id
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              }
            />

            {/* §9.214 - the grouping the roles tab has had since §9.176. The `slice(0, 200)` that
              * used to be here was a silent cap: 123 permissions fit under it, so nothing was lost,
              * but the row that fell off would have looked exactly like one that does not exist. */}
            <div className="space-y-2" data-override-rows>
              {groups.map((group) => {
                const rows = group.entries
                  .map((entry) => entry.permission)
                  .filter((permission) => {
                    const state = overrideState(permission, overrides)
                    const because = heldBecause(permission, { ...selected, overrides }, known)
                    if (scope === 'exceptions' && state === 'inherit') return false
                    if (scope === 'granted' && because === 'no') return false
                    return true
                  })
                  .filter((permission) =>
                    matchesSearch(
                      permission,
                      permissionLabel(permission, displayParentOf(permission, known)),
                      search,
                    ),
                  )
                if (rows.length === 0) return null

                // This tab exists for the rows that depart from the role, so that is what a
                // collapsed heading counts — the roles tab counts what is on, because that is what
                // a role is.
                const exceptions = rows.filter(
                  (permission) => overrideState(permission, overrides) !== 'inherit',
                ).length
                // §9.176's rule, inherited rather than re-decided: a typed search opens every group
                // that still has a row, or the hit is hidden while the screen says nothing matched.
                const isOpen = search.trim() !== '' || openedGroups.has(group.key)

                return (
                  <div
                    key={group.key}
                    className="rounded-2xl border border-border bg-card p-3 space-y-1"
                    data-override-group={group.key}
                  >
                    <button
                      type="button"
                      data-override-group-toggle={group.key}
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpenedGroups((current) => {
                          const next = new Set(current)
                          if (next.has(group.key)) next.delete(group.key)
                          else next.add(group.key)
                          return next
                        })
                      }
                      className="flex items-center gap-2 w-full text-left"
                    >
                      <ChevronRight
                        className={cn(
                          'w-4 h-4 shrink-0 text-muted-foreground transition-transform',
                          isOpen && 'rotate-90',
                        )}
                      />
                      <h3 className="text-sm font-bold truncate flex-1 min-w-0">{group.heading}</h3>
                      <span
                        className="text-[11px] text-muted-foreground tabular-nums shrink-0"
                        data-override-group-count={group.key}
                      >
                        {exceptions > 0 ? (
                          <span className="text-amber-500 font-bold">
                            {exceptions} {tt('výnimiek')}
                          </span>
                        ) : (
                          `${rows.length}`
                        )}
                      </span>
                    </button>

                    {isOpen &&
                      rows.map((permission) => {
                  const state = overrideState(permission, overrides)
                  const because = heldBecause(permission, { ...selected, overrides }, known)
                  const parent = displayParentOf(permission, known)
                  const description = permissionDescription(permission)
                  return (
                    <div
                      key={permission}
                      className="flex items-center gap-3 rounded-lg border border-border px-3 py-1.5"
                      data-override-row={permission}
                    >
                      <span className="min-w-0 flex-1">
                        {/* §9.152 - the §9.145 name, which this tab never got. */}
                        <span className="block text-xs font-semibold truncate">
                          {permissionLabel(permission, parent)}
                        </span>
                        {description !== null && (
                          <span className="block text-[10px] text-muted-foreground truncate">
                            {description}
                          </span>
                        )}
                        <span className="block text-[10px] text-muted-foreground font-mono truncate">
                          {permission}
                        </span>
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0" data-held-because={because}>
                        {because === 'role' && <T>z roly</T>}
                        {because === 'parent' && <T>cez nadradené</T>}
                        {because === 'override' && <T>výnimkou</T>}
                        {because === 'no' && <T>nemá</T>}
                      </span>
                      <button
                        type="button"
                        onClick={() => setLocal(withOverride(overrides, permission, cycleOverride(state)))}
                        aria-label={`${permission} - ${overrideLabel(state)}`}
                        data-override-state={state}
                        className={cn(
                          'px-2 py-1 rounded-lg border text-[10px] font-bold shrink-0 w-20',
                          state === 'grant' && 'border-emerald-500/50 text-emerald-500',
                          state === 'deny' && 'border-red-500/50 text-red-500',
                          state === 'inherit' && 'border-border text-muted-foreground',
                        )}
                      >
                        {overrideLabel(state)}
                      </button>
                    </div>
                  )
                      })}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function overrideLabel(state: OverrideState): string {
  if (state === 'grant') return tt('udeliť')
  if (state === 'deny') return tt('odobrať')
  return tt('podľa roly')
}

// ─── who changed it ──────────────────────────────────────────────────────────

function AuditTab() {
  const { data, isLoading } = useGetSettingsPermissionsAudit({ limit: 200 })
  const entries = useMemo(
    () => byMostRecent(((data ?? {}) as { entries?: AuditEntry[] }).entries ?? []),
    [data],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-8 text-center">
        <T>Nikto zatiaľ práva nemenil.</T>
      </p>
    )
  }

  return (
    <div className="space-y-1.5" data-audit>
      {entries.map((entry) => {
        const change = auditChange(entry)
        return (
          <div key={entry.id} className="rounded-xl border border-border bg-card px-3 py-2.5 space-y-1" data-audit-entry={entry.id}>
            <p className="text-xs">
              <strong>{entry.actorName}</strong> {actionText(entry.action)}{' '}
              <strong>{entry.targetName ?? entry.targetKey}</strong>
              <span className="text-muted-foreground"> · {formatStamp(entry.createdAt)}</span>
            </p>
            {/* Legacy prints the two snapshots as JSON; the reader wants the permission that moved. */}
            {change.added.length > 0 && (
              <p className="text-[11px] text-emerald-500" data-audit-added>
                + {change.added.join(', ')}
              </p>
            )}
            {change.removed.length > 0 && (
              <p className="text-[11px] text-red-500" data-audit-removed>
                − {change.removed.join(', ')}
              </p>
            )}
            {change.added.length === 0 && change.removed.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                <T>Bez zmeny v právach.</T>
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

function formatStamp(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return '-'
  return new Date(stamp).toLocaleString('sk-SK')
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
